/**
 * What one phone Control Panel row says about a pour, as plain data (pure,
 * no React). A row is:
 *   1. the pour name, with the problem on the right only when there is one
 *      ("2 not reporting", coloured);
 *   2. the project, how far into its planned monitoring the pour is ("day
 *      25 of 28", or "36 d" without a planned end) and how fresh the data
 *      is ("5 min ago");
 *   3. labelled tiles, each only when the pour measures it: Concrete max,
 *      Ambient (°C and %RH), and Strength (the lowest location; maturity
 *      when there is no curve);
 *   4. a bar to the next strength target ("Next: Stripping 20 MPa", "1 of 3
 *      reached").
 * and where it sits on the In progress tab:
 *   - list: the one list, in the server's (desktop) order, scheduled pours
 *     included;
 *   - review: probably finished: past its planned end, no reading for over
 *     a day, or every location's logging ended. Folded away as "Awaiting
 *     review"; a person opens one and stops it by hand (nothing is ever
 *     stopped for them);
 *   - other: Pending and Completed pours (their own tabs, or search).
 *
 * The planned end comes from the pour's own stop setting, as the stop rules
 * read it (services/pour_stop_rules.py): StopType '31' = XHours DAYS after
 * the start, or the preset stop time. The start is Started, else the
 * concrete start, else the planned logger start, because Started is often
 * empty. It only sorts and labels the list.
 *
 * The site state fields (OverLimitCount, OfflineCount, EndedCount,
 * NewestReadingAt, MaxCurrentTemp, ConcreteStart) and the roll-up fields
 * (AmbientTemp, AmbientHumidity, LowestStrength, LowestEA, MaturityBasis,
 * NextTarget*, Targets*) come only with health_detail=true and only on In
 * Progress rows. Without them (an older backend) the row falls back to
 * today's WarningCount / AnyStale roll-up and shows fewer tiles.
 *
 * Every string comes from t() with an English default; no em dashes.
 */
import type { Pour } from '../../services/pours';
import { formatInTimeZone, parseUtc } from '../../utils/datetime';
import type { IsoLike } from '../../utils/datetime';
import { describeMaturity, formatHalfUp, maturityShortLabel, resolveStrengthUnit } from '../../utils/maturityDisplay';
import { formatAgo, formatSpanShort, pourClockStart } from '../../utils/relativeTime';
import type { TranslateFn } from '../../utils/relativeTime';
import { isCompletedStatus, isInProgressStatus, isPendingStatus, isScheduledPour } from './pourListStatus';

/** The per-pour roll-up the list sends with health_detail (In Progress
 *  rows only). Optional: an older backend sends none of it. */
export interface PourListRollup {
  AmbientTemp?: number | null;
  AmbientHumidity?: number | null;
  LowestStrength?: number | null;
  /** 'above' / 'below' the curve's tested range; null inside it. */
  LowestStrengthBound?: 'above' | 'below' | string | null;
  LowestEA?: number | null;
  MaturityBasis?: 'strength' | 'maturity' | string | null;
  NextTargetName?: string | null;
  NextTargetValue?: number | null;
  NextTargetMaturity?: number | null;
  TargetsReached?: number | null;
  TargetsTotal?: number | null;
  StrengthUnit?: string | null;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** No reading for this long: the pour has gone quiet (probably finished). */
export const QUIET_AFTER_MS = DAY_MS;

export type PourFlagTone = 'error' | 'warning';
export type PourFlagIcon = 'overLimit' | 'offline' | 'sensor';

/** The problem on line 2, only when the pour needs the user. Icon and
 *  word, never colour alone. */
export interface PourRowFlag {
  text: string;
  tone: PourFlagTone;
  icon: PourFlagIcon;
}

/** Where the row sits on the In progress tab. */
export type PourRowSection = 'list' | 'review' | 'other';

/** One labelled reading on the row: "CONCRETE MAX 58.4 °C". */
export interface PourRowTile {
  key: 'concrete' | 'ambient' | 'ambientTemp' | 'humidity' | 'differential' | 'strength' | 'maturity';
  label: string;
  value: string;
  unit: string;
  /** A small line under the value: "61 %RH", "lowest location". */
  sub: string | null;
  /** Outside its limits: drawn red. */
  bad: boolean;
}

/** The bar to the next strength (or maturity) target. */
export interface PourRowTarget {
  /** "Stripping 20 MPa"; null once every target is reached. */
  next: string | null;
  /** 0..1, the lowest location's value against the next target. */
  fraction: number;
  reached: number;
  total: number;
}

export interface PourRowModel {
  title: string;
  /** The Pour ID (PourCode), as desktop's first column; '' when absent or
   *  already the title. */
  code: string;
  /** "Ballina, 24 Oct": the project and the start date (Started, else the
   *  concrete start, else the planned logger start). */
  projectLine: string;
  /** Line 2: "Ballina · day 2 of 7". */
  lead: string;
  /** On the right of line 1, only when something is wrong. */
  flag: PourRowFlag | null;
  /** Line 2's ending: "5 min ago", "planned end 29 Sept (24 d ago)", "no
   *  data for 3 d", "starts 24 Oct, 06:00", ... */
  fact: string | null;
  /** Labelled readings, in order; empty when the pour sent none. */
  tiles: PourRowTile[];
  target: PourRowTarget | null;
  /** Not started yet (In Progress, logger start ahead). */
  scheduled: boolean;
  section: PourRowSection;
  /** The whole row for a screen reader, the pour code included. */
  ariaLabel: string;
}

export interface PourRowOptions {
  /** The time freshness is judged against: the last successful list fetch,
   *  so a phone without a connection never files every pour as quiet from
   *  its own old data. Defaults to `now`. */
  freshnessNow?: number;
}

const numberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const timeMs = (value: IsoLike): number | null => {
  const ms = parseUtc(value).getTime();
  return Number.isNaN(ms) ? null : ms;
};

/** "24 Oct, 06:00" (or "24 Oct" without the time) in the pour's timezone,
 *  with the year when it is not the current year. */
const absoluteTime = (value: IsoLike, timeZone: string | null | undefined, now: number, withTime = true): string => {
  const yearOf = (v: IsoLike) =>
    formatInTimeZone(v, timeZone, { day: undefined, month: undefined, hour: undefined, minute: undefined });
  const sameYear = yearOf(value) === yearOf(now);
  return formatInTimeZone(value, timeZone, {
    day: 'numeric',
    month: 'short',
    year: sameYear ? undefined : 'numeric',
    ...(withTime ? {} : { hour: undefined, minute: undefined }),
  });
};

/** One decimal: "58.2 °C". */
const formatTemp = (value: number): string => `${value.toFixed(1)} °C`;

/** The pour's start for the list (Started, else the concrete start, else the
 *  planned logger start) as epoch ms. */
const startOf = (pour: Pour): number | null =>
  pourClockStart({ started: pour.Started, concreteStart: pour.ConcreteStart, loggerStart: pour.LoggerStart })?.at ?? null;

/**
 * The pour's planned end from its own stop setting: StopType '31' = XHours
 * days after the start, else the preset stop time. null when the setting
 * has no fixed end (e.g. '7', after the final targets) or the start is
 * unknown. Display and sorting only: nothing is ever stopped from it.
 */
export const plannedEndOf = (pour: Pour): { end: number; days: number } | null => {
  const start = startOf(pour);
  const days = numberOrNull(pour.XHours);
  if (String(pour.StopType ?? '').trim() === '31' && start !== null && days !== null && days > 0) {
    return { end: start + days * DAY_MS, days };
  }
  const preset = timeMs(pour.PreSetStopTime);
  if (preset !== null && start !== null && preset > start) {
    return { end: preset, days: Math.max(1, Math.round((preset - start) / DAY_MS)) };
  }
  return null;
};

/** The health roll-up of a row, as numbers (null when not sent). */
const healthOf = (pour: Pour) => {
  const offline = numberOrNull(pour.OfflineCount);
  const ended = numberOrNull(pour.EndedCount);
  return {
    // Monitoring now: In Progress and started.
    live: isInProgressStatus(pour.PourStatus) && !isScheduledPour(pour),
    locations: numberOrNull(pour.LoggerCount),
    overLimit: numberOrNull(pour.OverLimitCount),
    offline,
    ended,
    warnings: numberOrNull(pour.WarningCount) ?? 0,
    // The health_detail fields are filled (numbers) on In Progress rows.
    hasDetail: offline !== null || ended !== null,
    hasHealth: offline !== null || ended !== null || pour.WarningCount != null || pour.AnyStale != null,
    notReporting: offline !== null ? offline > 0 : !!pour.AnyStale,
  };
};

/** Why a live pour is probably finished, or null while it is running. */
type FinishedReason =
  | { kind: 'plannedEnd'; end: number }
  | { kind: 'quiet'; newest: number | null }
  | { kind: 'ended' };

const finishedReason = (pour: Pour, now: number, freshnessNow: number): FinishedReason | null => {
  const { locations, ended, hasDetail } = healthOf(pour);
  const planned = plannedEndOf(pour);
  if (planned && planned.end < now) return { kind: 'plannedEnd', end: planned.end };
  if (ended !== null && locations !== null && locations > 0 && ended >= locations) return { kind: 'ended' };
  const newest = timeMs(pour.NewestReadingAt);
  if (newest !== null) return freshnessNow - newest > QUIET_AFTER_MS ? { kind: 'quiet', newest } : null;
  // No reading at all: quiet once the pour started over a day ago.
  const start = startOf(pour);
  return hasDetail && start !== null && freshnessNow - start > QUIET_AFTER_MS ? { kind: 'quiet', newest: null } : null;
};

/** The worst thing about a running pour, as its flag; null when all is
 *  fine. */
const flagOf = (pour: Pour, t: TranslateFn): { flag: PourRowFlag; severity: number } | null => {
  const { overLimit, offline, warnings, notReporting } = healthOf(pour);
  if (overLimit !== null && overLimit > 0) {
    return {
      severity: 0,
      flag: {
        tone: 'error',
        icon: 'overLimit',
        text: t('mobile.controlPanel.card.outsideLimits', {
          count: overLimit,
          defaultValue_one: '{{count}} outside limits',
          defaultValue_other: '{{count}} outside limits',
        }),
      },
    };
  }
  if (notReporting) {
    return {
      severity: 1,
      flag: {
        tone: 'warning',
        icon: 'offline',
        text:
          offline !== null && offline > 0
            ? t('mobile.controlPanel.card.notReportingCount', {
                count: offline,
                defaultValue_one: '{{count}} not reporting',
                defaultValue_other: '{{count}} not reporting',
              })
            : t('mobile.controlPanel.card.notReporting', { defaultValue: 'Not reporting' }),
      },
    };
  }
  if (warnings > 0) {
    return {
      severity: 2,
      flag: {
        tone: 'warning',
        icon: 'sensor',
        text: t('mobile.controlPanel.card.loggerIssue', {
          count: warnings,
          defaultValue_one: '{{count}} sensor issue',
          defaultValue_other: '{{count}} sensor issues',
        }),
      },
    };
  }
  return null;
};

/** Plain-text t() for the rules that need no wording. */
const defaultT: TranslateFn = (key: string, options?: any) => options?.defaultValue ?? key;

/**
 * Whether a pour needs the user now: 'error' when a location is outside a
 * limit, 'warning' when one is not reporting or has a sensor issue; null
 * otherwise, for a probably finished pour, and for a pour that is not
 * monitoring now (Scheduled, Pending, Completed).
 */
export const pourAttentionLevel = (pour: Pour, now: number = Date.now()): 'error' | 'warning' | null => {
  const health = healthOf(pour);
  if (!health.live || !health.hasHealth) return null;
  if (finishedReason(pour, now, now)) return null;
  const found = flagOf(pour, defaultT);
  return found ? found.flag.tone : null;
};

export const pourNeedsAttention = (pour: Pour, now?: number): boolean => pourAttentionLevel(pour, now) !== null;


/** The labelled readings of a pour, in order: Concrete max, Ambient (one
 *  tile with %RH under it, or two tiles when the pour measures nothing
 *  else), then Strength or maturity. A tile shows only with a value. */
export const pourTiles = (pour: Pour, t: TranslateFn): PourRowTile[] => {
  const rollup = pour as Pour & PourListRollup;
  const tiles: PourRowTile[] = [];
  const concrete = numberOrNull(pour.MaxCurrentTemp);
  const ambientT = numberOrNull(rollup.AmbientTemp);
  const ambientRh = numberOrNull(rollup.AmbientHumidity);
  const strength = numberOrNull(rollup.LowestStrength);
  const maturity = numberOrNull(rollup.LowestEA);
  const overLimit = numberOrNull(pour.OverLimitCount);
  const strengthBasis = rollup.MaturityBasis === 'strength' && strength !== null;
  const hasMaturityTile = strengthBasis || maturity !== null;

  if (concrete !== null) {
    tiles.push({
      key: 'concrete',
      label: t('mobile.controlPanel.tile.concrete', { defaultValue: 'Concrete max' }),
      value: concrete.toFixed(1),
      unit: '°C',
      sub: null,
      bad: overLimit !== null && overLimit > 0,
    });
  }
  const rhText = (rh: number) => `${Math.round(rh)} %RH`;
  if (ambientT !== null && (concrete !== null || hasMaturityTile)) {
    tiles.push({
      key: 'ambient',
      label: t('mobile.controlPanel.tile.ambient', { defaultValue: 'Ambient' }),
      value: ambientT.toFixed(1),
      unit: '°C',
      sub: ambientRh !== null ? rhText(ambientRh) : null,
      bad: false,
    });
  } else if (ambientT !== null || ambientRh !== null) {
    // An ambient-only pour: temperature and humidity get a tile each.
    if (ambientT !== null) {
      tiles.push({
        key: 'ambientTemp',
        label: t('mobile.controlPanel.tile.ambientTemp', { defaultValue: 'Ambient temp' }),
        value: ambientT.toFixed(1),
        unit: '°C',
        sub: null,
        bad: false,
      });
    }
    if (ambientRh !== null) {
      tiles.push({
        key: 'humidity',
        label: t('mobile.controlPanel.tile.humidity', { defaultValue: 'Humidity' }),
        value: String(Math.round(ambientRh)),
        unit: '%RH',
        sub: null,
        bad: false,
      });
    }
  }
  const lowest = t('mobile.controlPanel.tile.lowest', { defaultValue: 'lowest location' });
  if (strengthBasis) {
    // Outside the tested range: "> 28.9", "< 3.3", as desktop shows it.
    const bound = rollup.LowestStrengthBound === 'above' ? '> ' : rollup.LowestStrengthBound === 'below' ? '< ' : '';
    tiles.push({
      key: 'strength',
      label: t('mobile.controlPanel.tile.strength', { defaultValue: 'Strength' }),
      value: `${bound}${formatHalfUp(strength as number, 1)}`,
      unit: resolveStrengthUnit(rollup.StrengthUnit),
      sub: lowest,
      bad: false,
    });
  } else if (maturity !== null) {
    const meta = describeMaturity(pour.MaturityMethod, pour.SpecifiedTemp);
    tiles.push({
      key: 'maturity',
      label: maturityShortLabel(meta),
      value: formatHalfUp(maturity, 1),
      unit: meta.unit,
      sub: lowest,
      bad: false,
    });
  }
  return tiles;
};

/** The bar to the lowest location's next target; null when the pour has no
 *  targets or sent none. */
export const pourTarget = (pour: Pour): PourRowTarget | null => {
  const rollup = pour as Pour & PourListRollup;
  const total = numberOrNull(rollup.TargetsTotal);
  if (total === null || total <= 0) return null;
  const reached = Math.min(total, Math.max(0, numberOrNull(rollup.TargetsReached) ?? 0));
  const strengthBasis = rollup.MaturityBasis === 'strength';
  const current = numberOrNull(strengthBasis ? rollup.LowestStrength : rollup.LowestEA);
  const goal = numberOrNull(rollup.NextTargetValue);
  const name = rollup.NextTargetName && String(rollup.NextTargetName).trim();
  if (reached >= total || goal === null) {
    return { next: null, fraction: reached >= total ? 1 : 0, reached, total };
  }
  const unit = strengthBasis
    ? resolveStrengthUnit(rollup.StrengthUnit)
    : describeMaturity(pour.MaturityMethod, pour.SpecifiedTemp).unit;
  const value = `${formatHalfUp(goal, 1)} ${unit}`;
  // The bar is maturity over the target's maturity, as the dashboard's (the
  // maturity decides when a target is reached); the value's own ratio only
  // when an older backend sends no NextTargetMaturity.
  const ea = numberOrNull(rollup.LowestEA);
  const goalMaturity = numberOrNull(rollup.NextTargetMaturity);
  const [have, need] = ea !== null && goalMaturity !== null && goalMaturity > 0 ? [ea, goalMaturity] : [current, goal];
  return {
    next: name ? `${name} ${value}` : value,
    fraction: have !== null && need > 0 ? Math.max(0, Math.min(1, have / need)) : 0,
    reached,
    total,
  };
};

/** How far into its planned monitoring a started pour is: "day 25 of 28",
 *  else the time since its start ("36 d"); '' before the start. The Control
 *  Panel row and the dashboard's status line share it. */
export const pourProgressText = (pour: Pour, now: number, t: TranslateFn): string => {
  const start = startOf(pour);
  if (start === null || start > now) return '';
  const planned = plannedEndOf(pour);
  return planned
    ? t('mobile.controlPanel.row.dayOf', {
        defaultValue: 'day {{day}} of {{total}}',
        day: Math.min(planned.days, Math.floor((now - start) / DAY_MS) + 1),
        total: planned.days,
      })
    : formatSpanShort(now - start, t);
};

/**
 * Build one row. `now` drives the live text ("day 25 of 28", "5 min ago");
 * whether a pour has gone quiet is judged against options.freshnessNow, the
 * last successful fetch.
 */
export const buildPourRow = (
  pour: Pour,
  now: number,
  t: TranslateFn,
  options: PourRowOptions = {},
): PourRowModel => {
  const freshnessNow = options.freshnessNow ?? now;
  const title = pour.PourName || pour.PourCode || '-';
  const project = pour.ProjectName && String(pour.ProjectName).trim() ? String(pour.ProjectName).trim() : '';
  const tz = pour.TimeZoneIANA;
  const pourCode = pour.PourCode && String(pour.PourCode).trim() ? String(pour.PourCode).trim() : '';
  const code = pour.PourName && pourCode ? pourCode : '';
  const startAt = startOf(pour);
  const projectLine = [project, startAt !== null ? absoluteTime(startAt, tz, now, false) : ''].filter(Boolean).join(', ');

  const row = (
    fields: Omit<PourRowModel, 'title' | 'code' | 'projectLine' | 'ariaLabel' | 'tiles' | 'target' | 'scheduled'> &
      Partial<Pick<PourRowModel, 'tiles' | 'target' | 'scheduled'>>,
  ): PourRowModel => {
    const tiles = fields.tiles ?? [];
    const target = fields.target ?? null;
    return {
      title,
      code,
      projectLine,
      tiles,
      target,
      scheduled: false,
      ...fields,
      ariaLabel: [
        title,
        pour.PourCode,
        fields.flag?.text,
        [fields.lead, fields.fact].filter(Boolean).join(' · '),
        ...tiles.map((tile) => `${tile.label} ${tile.value} ${tile.unit}${tile.sub ? `, ${tile.sub}` : ''}`),
        target?.next ? `Next ${target.next}, ${target.reached} of ${target.total} reached` : null,
      ]
        .filter(Boolean)
        .join('. '),
    };
  };

  if (isPendingStatus(pour.PourStatus)) {
    return row({
      lead: project,
      flag: null,
      fact: t('mobile.controlPanel.row.notStarted', { defaultValue: 'not started' }),
      section: 'other',
    });
  }

  const start = startOf(pour);
  const startsAt = isScheduledPour(pour)
    ? pour.StartsAt || pour.LoggerStart
    : isInProgressStatus(pour.PourStatus) && start !== null && start > now
    ? // A start still ahead that the server did not flag as Scheduled.
      start
    : null;
  if (startsAt != null && timeMs(startsAt) !== null) {
    return row({
      lead: project,
      flag: null,
      fact: t('mobile.controlPanel.row.startsAt', { defaultValue: 'starts {{time}}', time: absoluteTime(startsAt, tz, now) }),
      scheduled: true,
      section: isInProgressStatus(pour.PourStatus) ? 'list' : 'other',
    });
  }

  if (isCompletedStatus(pour.PourStatus)) {
    const end = pour.LoggerEnd || pour.Finished;
    return row({
      lead: project,
      flag: null,
      fact:
        timeMs(end) !== null
          ? t('mobile.controlPanel.row.ended', { defaultValue: 'ended {{time}}', time: absoluteTime(end, tz, now, false) })
          : t('mobile.controlPanel.row.completed', { defaultValue: 'completed' }),
      section: 'other',
    });
  }

  // ---- In Progress, monitoring ---------------------------------------------
  const { hasHealth, hasDetail, locations } = healthOf(pour);
  const newest = timeMs(pour.NewestReadingAt);
  const tiles = pourTiles(pour, t);
  const target = pourTarget(pour);

  const finished = hasHealth ? finishedReason(pour, now, freshnessNow) : null;
  if (finished) {
    let fact: string;
    if (finished.kind === 'plannedEnd') {
      fact = t('mobile.controlPanel.row.plannedEnd', {
        defaultValue: 'planned end {{date}} ({{ago}})',
        date: absoluteTime(finished.end, tz, now, false),
        ago: formatAgo(finished.end, now, t),
      });
    } else if (finished.kind === 'ended') {
      fact = t('mobile.controlPanel.row.loggingEnded', { defaultValue: 'logging ended' });
    } else {
      fact = t('mobile.controlPanel.row.noReadingsFor', {
        defaultValue: 'no data for {{duration}}',
        duration: formatSpanShort(now - (finished.newest ?? start ?? now), t),
      });
    }
    // Last values, never judged: nothing is red on a pour awaiting review.
    return row({
      lead: project,
      flag: null,
      fact,
      tiles: tiles.map((tile) => ({ ...tile, bad: false })),
      section: 'review',
    });
  }

  const progress = pourProgressText(pour, now, t);
  const lead = [project, progress].filter(Boolean).join(' · ');
  const found = hasHealth ? flagOf(pour, t) : null;

  // Just started, nothing received yet.
  const fact =
    hasDetail && newest === null && (locations === null || locations > 0)
      ? t('mobile.controlPanel.row.awaiting', { defaultValue: 'awaiting first reading' })
      : newest !== null
      ? formatAgo(newest, now, t)
      : null;

  return row({ lead, flag: found?.flag ?? null, fact, tiles, target, section: 'list' });
};

/** Rows of one list for the In progress tab: the list in the server's
 *  (desktop) order, and the probably finished ones folded away. */
export const groupPourRows = <T extends { model: PourRowModel }>(
  rows: ReadonlyArray<T>,
): { list: T[]; review: T[]; other: T[] } => ({
  list: rows.filter((r) => r.model.section === 'list'),
  review: rows.filter((r) => r.model.section === 'review'),
  other: rows.filter((r) => r.model.section === 'other'),
});
